import clsx from "clsx";
import { Markdown } from "@client/components/Markdown";
import { useT } from "@client/i18n";
import type { PrepLecture } from "@content/prepSchema";
import { AnchorLine } from "./AnchorLine";
import { BookBox } from "./BookBox";
import { Panel } from "./Panel";

type History = NonNullable<PrepLecture["history"]>;

/** The eras scroll sideways inside their own track, never the page. */
export function HistoryStrip({
  data,
  activePreset,
  onShow,
  className,
}: {
  data: History;
  activePreset: string | null;
  onShow(presetId: string): void;
  className?: string;
}) {
  const t = useT();
  return (
    <Panel title={t("teacher.prep.panel.history")} {...(className ? { className } : {})}>
      <BookBox>{data.bookBox}</BookBox>
      <div
        role="group"
        aria-label={t("teacher.prep.panel.history")}
        tabIndex={0}
        className="flex max-w-full snap-x gap-3 overflow-x-auto pb-2"
      >
        {data.eras.map((era) => {
          const active = era.preset !== undefined && era.preset === activePreset;
          return (
            <article
              key={era.id}
              className={clsx(
                "w-64 shrink-0 snap-start space-y-2 rounded-lg border p-3",
                active
                  ? "border-accent-600 bg-accent-50 dark:border-accent-400 dark:bg-accent-950"
                  : "border-ink-200 dark:border-ink-700",
              )}
            >
              <button
                type="button"
                disabled={era.preset === undefined}
                aria-pressed={active}
                onClick={() => era.preset && onShow(era.preset)}
                className="w-full rounded text-left font-medium text-ink-900 hover:underline disabled:no-underline dark:text-ink-50"
              >
                <span className="block text-xs font-normal text-ink-600 dark:text-ink-400">
                  {era.date}
                </span>
                {era.title}
              </button>
              <Markdown variant="compact">{era.body}</Markdown>
              <AnchorLine book={era.book} beyondBook={era.beyondBook} />
            </article>
          );
        })}
      </div>
    </Panel>
  );
}
